import React, { useState, useEffect, useMemo } from 'react'
import {
  speakerApi,
  getStoredSpeakerPassword,
  setStoredSpeakerPassword,
  clearStoredSpeakerPassword,
  ALLOWED_UPLOAD_EXTENSIONS,
  MAX_UPLOAD_FILE_SIZE
} from '../utils/api'
import { getRecentSundayDateStrs, formatDateDisplay } from '../utils/sundayDisplay'
import { t } from '../utils/translations'
import AppBranding from '../components/AppBranding'

const CHURCHES = ['KTC', 'SSC', 'WIC', 'STC']
const ENTRY_TYPES = [
  { value: 'SundaySchool', labelKey: 'sundaySchool' },
  { value: 'Sermon', labelKey: 'sermon' }
]

export default function SpeakerUploadPage() {
  const [lang, setLang] = useState('zh')
  const sundayOptions = useMemo(() => getRecentSundayDateStrs(6), [])

  const [church, setChurch] = useState('KTC')
  const [eventDate, setEventDate] = useState(sundayOptions[0] || '')
  const [type, setType] = useState('SundaySchool')
  const [entryInfo, setEntryInfo] = useState(null)
  const [uploadPassword, setUploadPassword] = useState(() => getStoredSpeakerPassword() || '')
  const [rememberPassword, setRememberPassword] = useState(Boolean(getStoredSpeakerPassword()))
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  useEffect(() => {
    if (!church || !eventDate || !type) return
    let cancelled = false

    speakerApi.lookupEntry(church, eventDate, type)
      .then(res => {
        if (!cancelled) setEntryInfo(res.data.data?.entry ?? null)
      })
      .catch(() => {
        if (!cancelled) setEntryInfo(null)
      })

    return () => { cancelled = true }
  }, [church, eventDate, type])

  const handleClearPassword = () => {
    clearStoredSpeakerPassword()
    setUploadPassword('')
    setRememberPassword(false)
  }

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return

    const password = uploadPassword.trim()
    if (!password) {
      setError(lang === 'zh' ? '請輸入上傳密碼' : 'Please enter upload password')
      return
    }

    const ext = file.name.split('.').pop()?.toLowerCase()
    if (!ALLOWED_UPLOAD_EXTENSIONS.includes(ext)) {
      setError(lang === 'zh'
        ? `不支援的檔案類型。允許：${ALLOWED_UPLOAD_EXTENSIONS.join(', ')}`
        : `Unsupported file type. Allowed: ${ALLOWED_UPLOAD_EXTENSIONS.join(', ')}`)
      return
    }
    if (file.size > MAX_UPLOAD_FILE_SIZE) {
      setError(lang === 'zh' ? '檔案太大（上限 50MB）' : 'File too large (max 50MB)')
      return
    }

    setError('')
    setSuccess('')
    setUploading(true)

    try {
      const result = await speakerApi.uploadWithPresignedUrl(file, {
        church,
        eventDate,
        speaker: entryInfo?.speaker || '',
        type,
        password
      })

      if (rememberPassword) {
        setStoredSpeakerPassword(password)
      }

      if (result.linked) {
        setSuccess(lang === 'zh'
          ? `上傳成功，已連結至條目：${result.filename}`
          : `Uploaded and linked: ${result.filename}`)
      } else {
        setSuccess(lang === 'zh'
          ? `上傳成功（${result.filename}）。尚未找到對應條目，同工可稍後連結。`
          : `Uploaded (${result.filename}). No matching entry — staff can link later.`)
      }

      const lookup = await speakerApi.lookupEntry(church, eventDate, type)
      setEntryInfo(lookup.data.data?.entry ?? null)
    } catch (err) {
      console.error('Speaker upload failed:', err)
      if (err.response?.status === 401) {
        clearStoredSpeakerPassword()
        setRememberPassword(false)
        setError(lang === 'zh' ? '上傳密碼錯誤' : 'Incorrect upload password')
      } else {
        setError(err.message || (lang === 'zh' ? '上傳失敗' : 'Upload failed'))
      }
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="speaker-upload-page">
      <div className="speaker-card speaker-card-wide">
        <div className="page-top">
          <AppBranding lang={lang} />
          <div className="top-actions">
            <button type="button" className="btn-link" onClick={() => setLang(lang === 'zh' ? 'en' : 'zh')}>
              {lang === 'zh' ? 'English' : '中文'}
            </button>
            {rememberPassword && (
              <button type="button" className="btn-link" onClick={handleClearPassword}>
                {lang === 'zh' ? '清除已記住密碼' : 'Clear saved password'}
              </button>
            )}
          </div>
        </div>

        <h2>{lang === 'zh' ? '講員上傳附件' : 'Speaker File Upload'}</h2>

        {error && <div className="message message-error">{error}</div>}
        {success && <div className="message message-success">{success}</div>}

        <div className="form-grid">
          <div className="form-group">
            <label>{t('church', lang)}</label>
            <select value={church} onChange={e => setChurch(e.target.value)}>
              {CHURCHES.map(c => (
                <option key={c} value={c}>{t(c, lang)}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>{t('eventDate', lang)}</label>
            <select value={eventDate} onChange={e => setEventDate(e.target.value)}>
              {sundayOptions.map(d => (
                <option key={d} value={d}>{formatDateDisplay(d, lang)}</option>
              ))}
            </select>
          </div>

          <div className="form-group full-width">
            <label>{t('type', lang)}</label>
            <select value={type} onChange={e => setType(e.target.value)}>
              {ENTRY_TYPES.map(opt => (
                <option key={opt.value} value={opt.value}>{t(opt.labelKey, lang)}</option>
              ))}
            </select>
          </div>

          {entryInfo ? (
            <div className="entry-hint message message-info full-width">
              {lang === 'zh'
                ? `已找到條目：${entryInfo.title}${entryInfo.speaker ? `（${entryInfo.speaker}）` : ''}，附件 ${entryInfo.file_count || 0} 個`
                : `Entry found: ${entryInfo.title}${entryInfo.speaker ? ` (${entryInfo.speaker})` : ''}, ${entryInfo.file_count || 0} file(s)`}
            </div>
          ) : (
            <div className="entry-hint message message-warning full-width">
              {lang === 'zh'
                ? '尚未找到對應條目，檔案仍會上傳至伺服器，同工可稍後連結。'
                : 'No matching entry yet — file will still upload; staff can link later.'}
            </div>
          )}

          <div className="form-group full-width">
            <label>{lang === 'zh' ? '上傳密碼' : 'Upload password'}</label>
            <input
              type="password"
              value={uploadPassword}
              onChange={e => setUploadPassword(e.target.value)}
              autoComplete="current-password"
              placeholder={lang === 'zh' ? '上傳時需要' : 'Required to upload'}
            />
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={rememberPassword}
                onChange={e => setRememberPassword(e.target.checked)}
              />
              {lang === 'zh' ? '記住密碼（此瀏覽器）' : 'Remember password on this browser'}
            </label>
          </div>

          <div className="form-group full-width">
            <label>{lang === 'zh' ? '選擇檔案' : 'Choose file'}</label>
            <input
              type="file"
              accept={ALLOWED_UPLOAD_EXTENSIONS.map(ext => `.${ext}`).join(',')}
              onChange={handleFileChange}
              disabled={uploading}
            />
            <p className="hint">
              {lang === 'zh'
                ? `支援：${ALLOWED_UPLOAD_EXTENSIONS.join(', ')}（最大 50MB）`
                : `Allowed: ${ALLOWED_UPLOAD_EXTENSIONS.join(', ')} (max 50MB)`}
            </p>
            {uploading && <p className="hint">{lang === 'zh' ? '正在上傳…' : 'Uploading…'}</p>}
          </div>
        </div>
      </div>
      <style jsx>{pageStyles}</style>
    </div>
  )
}

const pageStyles = `
  .speaker-upload-page {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px 16px;
    background: var(--bg-primary, #0f172a);
  }
  .speaker-card {
    width: 100%;
    max-width: 420px;
    background: var(--bg-card, #1e293b);
    border-radius: 12px;
    padding: 24px;
    border: 1px solid var(--border-color, #334155);
  }
  .speaker-card-wide { max-width: 520px; }
  .page-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 16px;
  }
  .top-actions { display: flex; gap: 12px; flex-wrap: wrap; justify-content: flex-end; }
  .btn-link {
    background: none;
    border: none;
    color: var(--text-secondary, #94a3b8);
    cursor: pointer;
    font-size: 0.875rem;
  }
  .btn-link:hover { color: var(--text-primary, #f1f5f9); }
  h2 { margin: 0 0 16px; font-size: 1.25rem; }
  .hint { font-size: 0.875rem; color: var(--text-secondary); margin: 8px 0 0; }
  .form-grid { display: flex; flex-direction: column; gap: 16px; }
  .form-group { display: flex; flex-direction: column; gap: 8px; }
  .form-group.full-width { width: 100%; }
  .form-group label { font-size: 0.875rem; color: var(--text-secondary); }
  .checkbox-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.875rem;
    color: var(--text-secondary);
    cursor: pointer;
  }
  .checkbox-label input { width: auto; }
  .form-group select,
  .form-group input[type="password"],
  .form-group input[type="file"] {
    width: 100%;
    padding: 10px 12px;
    border-radius: 6px;
    border: 2px solid var(--input-border, var(--border-color));
    background: var(--bg-input, var(--bg-tertiary, #0f172a));
    color: var(--text-primary);
  }
  .message {
    padding: 12px;
    border-radius: 6px;
    margin-bottom: 16px;
    font-size: 0.875rem;
  }
  .message-error { background: rgba(239, 68, 68, 0.15); color: #fca5a5; }
  .message-success { background: rgba(34, 197, 94, 0.15); color: #86efac; }
  .message-info { background: rgba(193, 43, 32, 0.12); color: var(--accent-primary); font-weight: 600; }
  .message-warning { background: rgba(234, 179, 8, 0.15); color: #fde047; }
  .entry-hint { margin-bottom: 0; }
`
